<template>
    <transition
        name="fade"
        mode="out-in"
    >
        <Suspense>
            <template #fallback>
                <TabsPanelSpinner />
            </template>

            <SuspenseUntil :true="!initialLoadInProgress">
                <PostCreate
                    :context-id="group.id"
                    context-type="group"
                    show-buttons-label
                    class="mb-5"
                    @created="addPostToFeed"
                ></PostCreate>

                <EmptyFeed
                    v-if="feedIsEmpty"
                    class="mt-6 md:mt-10"
                >
                    <template #header>
                        {{ t('group:emptyFeed.header') }}
                    </template>
                    <template #image>
                        <img
                            class="mx-auto w-48"
                            src="/images/mascots/sleep.png"
                        />
                    </template>
                    <template #title>
                        {{ t('group:emptyFeed.discussions.title') }}
                    </template>
                    <template #text>
                        {{ t('group:emptyFeed.discussions.text') }}
                    </template>
                    <template #button>
                        <BtnCta
                            :label="t('group:emptyFeed.discussions.cta')"
                            href="#"
                        />
                    </template>
                </EmptyFeed>

                <transition-group
                    v-else
                    name="fade"
                >
                    <template
                        v-for="feedItem in feedList"
                        :key="getId(feedItem)"
                    >
                        <!-- eslint-disable-next-line vue/require-component-is -->
                        <component
                            :is="componentType(feedItem)"
                            v-bind="componentProps(feedItem)"
                            class="mb-4"
                        ></component>
                    </template>
                    <Spinner
                        v-if="feedProgress"
                        appearance="circular"
                        size="xs"
                        color="blue"
                    ></Spinner>
                </transition-group>
            </SuspenseUntil>
        </Suspense>
    </transition>
    <InfiniteLoading
        :distance="2 * windowSize.height"
        @infinite="loadNextPage"
    >
    </InfiniteLoading>
</template>

<script>
import { mapGetters } from 'vuex';
import InfiniteLoading from 'v3-infinite-loading';
import EmptyFeed from '@components/EmptyFeed';
import PostCreate from '@components/PostCreate';
import PostItem from '@components/PostItem';
import TabsPanelSpinner from '@components/TabsPanelTab/TabsPanelSpinner.vue';
import { IdGenerator } from '@/helpers/IdGenerator';
import SuspenseUntil from '@components/SuspenseUntil';
import { Xandr, XandrStickyAd } from '@components/Xandr';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'GroupShowDiscussion',
    components: {
        SuspenseUntil,
        InfiniteLoading,
        EmptyFeed,
        PostCreate,

        PostItem,
        TabsPanelSpinner,
        Xandr,
        XandrStickyAd,
    },
    setup() {
        const { t } = useTranslation(['group', 'post']);
        return { t };
    },
    data() {
        return {
            currentPage: 0,
            initialLoadInProgress: true,
        };
    },
    computed: {
        ...mapGetters('group', [
            'feedList',
            'feedProgress',
            'feedCanLoadMore',
            'group',
        ]),
        ...mapGetters('ui', ['screen', 'windowSize']),
        ...mapGetters('auth', ['isLoggedIn']),
        feedIsEmpty() {
            return this.feedList.length === 0;
        },
    },
    created() {
        this.loadFeed();
    },
    methods: {
        getId: IdGenerator.get,
        componentProps(feedItem) {
            if (feedItem.type === 'post') {
                return { post: feedItem };
            }
            if (feedItem.type === 'xandrAd' || feedItem.type === 'xandrStickyAd') {
                return {
                    xandrWrapper: `${feedItem.adType}Wrapper`,
                    xandrSlot: feedItem.adType,
                    xandrSizes: feedItem.sizes,
                    adOnDemand: true,
                    isInFeedAd: feedItem?.inFeedAd,
                };
            }

            return {};
        },
        componentType(feedItem) {
            if (feedItem.type === 'post') {
                return PostItem;
            }
            if (feedItem.type === 'xandrAd') {
                return Xandr;
            }
            if (feedItem.type === 'xandrStickyAd') {
                return XandrStickyAd;
            }

            return undefined;
        },
        loadNextPage() {
            if (this.isLoggedIn) {
                this.loadFeed(this.currentPage + 1);
            }
        },
        loadFeed(page = 1) {
            // eslint-disable-next-line no-underscore-dangle
            if (this.feedProgress || !this.feedCanLoadMore || this._inactive) {
                return;
            }
            this.currentPage = page;

            this.$store.dispatch('group/retrieveFeed', { page }).then(() => {
                this.initialLoadInProgress = false;
            });
        },
        addPostToFeed(post) {
            this.$store.dispatch('group/addPostToFeed', post);
        },
    },
};
</script>
